import {
  assignPrototypeAPIs,
  type Cell,
  type CellData,
  type Column,
  type Row,
  type RowData,
  type TableFeature,
  type TableFeatures,
} from '@tanstack/react-table';

import { defaultComparators } from './default-comparators.js';
import { normalizeThresholdRules } from './normalize-rules.js';
import type {
  DataTableColumnThreshold,
  DataTableThresholdsNormalizedRule,
  RowThresholdsFeatureConstructors,
} from './threshold-types.js';
import {
  isAccessorFnColumnDef,
  isAccessorKeyColumnDef,
} from '../../hooks/useTable/datatable-typeguards.js';

/**
 * Helper function that will check if all rules of a given threshold match.
 * @internal
 */
function matchesThreshold<TData extends RowData>(
  threshold: DataTableThresholdsNormalizedRule<TData>,
  rowData: TData,
): boolean {
  return threshold.rules.every((rule) => {
    const compareValue = rule.accessor?.(rowData) ?? undefined;
    if (typeof rule.comparator === 'string') {
      return defaultComparators[rule.comparator](rule.value, compareValue);
    }
    return rule.comparator(rowData);
  });
}

/**
 * Helper function to get the pill color for the row threshold.
 * @internal
 */
export function row_getThresholdPill<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  row: Row<TFeatures, TData>,
  rowThresholds: DataTableThresholdsNormalizedRule<TData>[] | undefined,
): string | null {
  if (rowThresholds === undefined || rowThresholds.length === 0) {
    return null;
  }

  // Check from the back, because the last one matching one should be
  // the one applying, if there are more than one rules applied.
  const pillColor = rowThresholds.findLast(
    (threshold) =>
      threshold.type === 'pill' && matchesThreshold(threshold, row.original),
  )?.color;

  return pillColor ?? null;
}

/**
 * Helper function to get the threshold color and backgroundColor for the cell.
 * @internal
 */
export function row_getThresholdColors<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  row: Row<TFeatures, TData>,
  rowThresholds: DataTableThresholdsNormalizedRule<TData>[] | undefined,
): { color?: string; backgroundColor?: string } | null {
  if (rowThresholds === undefined || rowThresholds.length === 0) {
    return null;
  }

  // Check from the back, because the last one matching one should be
  // the one applying, if there are more than one rules applied.
  const matchedThreshold = rowThresholds.findLast(
    (threshold) =>
      threshold.type === 'highlight' &&
      matchesThreshold(threshold, row.original),
  );

  if (matchedThreshold) {
    return {
      color: matchedThreshold.color,
      backgroundColor: matchedThreshold.backgroundColor,
    };
  }
  return null;
}

/**
 * Helper function to get the threshold rules for the column.
 * @internal
 */
export function column_getThresholdRules<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  column: Column<TFeatures, TData>,
  thresholds: DataTableColumnThreshold<TData>[] | undefined,
): DataTableThresholdsNormalizedRule<TData>[] | null {
  if (thresholds === undefined || thresholds.length === 0) {
    return null;
  }

  const columnDef = column.columnDef;

  // Calculate the fallback accessor that should be used if there is
  // non given on the threshold rule.
  let fallbackAccessor:
    | string
    | number
    | symbol
    | ((row: TData) => unknown)
    | undefined;
  if (isAccessorKeyColumnDef<TFeatures, TData>(columnDef)) {
    fallbackAccessor = columnDef.accessorKey;
  }
  if (isAccessorFnColumnDef<TFeatures, TData>(columnDef)) {
    const accessorFn = columnDef.accessorFn;
    fallbackAccessor = (row: TData) => accessorFn(row, 0);
  }

  return normalizeThresholdRules(thresholds, fallbackAccessor);
}

/**
 * Helper function to get the threshold color and backgroundColor for the cell.
 * @internal
 */
export function cell_getThresholdColors<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData,
>(cell: Cell<TFeatures, TData, TValue>) {
  const columnThresholds = cell.column.getThresholdRules();

  const matchedColors: { color?: string; backgroundColor?: string } = {
    color: undefined,
    backgroundColor: undefined,
  };

  if (columnThresholds) {
    // Check from the back, because the last one matching one should be
    // the one applying, if there are more than one rules applied.
    const matchedColumnThreshold = columnThresholds.findLast((threshold) =>
      matchesThreshold(threshold, cell.row.original),
    );

    if (matchedColumnThreshold) {
      matchedColors.color = matchedColumnThreshold.color;
      matchedColors.backgroundColor = matchedColumnThreshold.backgroundColor;
      return matchedColors;
    }
  }

  // If no column threshold rules matched, we can check if the row threshold
  // rule will match.
  const rowThresholds = cell.row.getThresholdColors();
  if (rowThresholds) {
    matchedColors.color = rowThresholds.color;
    matchedColors.backgroundColor = rowThresholds.backgroundColor;
  }
  return matchedColors;
}

/**
 * Constructs the column row thresholds for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructRowThresholdsFeature<
  TData extends RowData,
>(): TableFeature<RowThresholdsFeatureConstructors<TData>> {
  return {
    assignCellPrototype: (prototype, table) => {
      assignPrototypeAPIs('DataTableRowThresholdsFeature', prototype, table, {
        cell_getThresholdColors: {
          fn: (cell) => cell_getThresholdColors(cell),
        },
      });
    },
    assignColumnPrototype: (prototype, table) => {
      assignPrototypeAPIs('DataTableRowThresholdsFeature', prototype, table, {
        column_getThresholdRules: {
          fn: (column, thresholds) =>
            column_getThresholdRules(column, thresholds),
          memoDeps: (column) => [column.columnDef.thresholds],
        },
      });
    },
    assignRowPrototype: (prototype, table) => {
      assignPrototypeAPIs('DataTableRowThresholdsFeature', prototype, table, {
        row_getThresholdPill: {
          fn: (row, thresholds) => row_getThresholdPill(row, thresholds),
          memoDeps: (row) => [table.options.rowThresholds, row.id, row.index],
        },
        row_getThresholdColors: {
          fn: (row, thresholds) => row_getThresholdColors(row, thresholds),
          memoDeps: (row) => [table.options.rowThresholds, row.id, row.index],
        },
      });
    },
  };
}

/**
 * Feature to handle thresholds to help in row and cell highlighting.
 * @internal
 */
export const DataTableRowThresholdsFeature = constructRowThresholdsFeature();
